import React from 'react';
import { Users, Shield, Cog, Brain, CheckCircle, Network, Database, Cloud, PenTool as Tool, Zap, Clock, BarChart as ChartBar, HeartHandshake, Workflow, Settings } from 'lucide-react';
import ServicePage from './ServicePage';

const coManagedITData = {
  title: "Co-Managed IT Services",
  description: "Supercharge your internal IT team with our flexible co-managed IT solutions. Get enterprise-grade support, specialized expertise, and scalable resources exactly when you need them.",
  features: [
    {
      icon: Users,
      title: "Strategic Team Augmentation",
      description: "Seamlessly extend your IT capabilities with specialized expertise",
      details: [
        "Access to certified IT specialists across multiple domains",
        "Flexible resource scaling based on project needs",
        "Knowledge transfer and team upskilling programs",
        "24/7 support coverage for your internal team",
        "Custom staffing solutions for specific projects"
      ]
    },
    {
      icon: Shield,
      title: "Enterprise-Grade Security",
      description: "Strengthen your security posture with advanced protection",
      details: [
        "Comprehensive security assessments and gap analysis",
        "Advanced threat detection and response",
        "Compliance management and documentation",
        "Security awareness training programs",
        "Incident response planning and support"
      ]
    },
    {
      icon: Workflow,
      title: "Process Optimization",
      description: "Streamline operations with proven methodologies",
      details: [
        "ITIL-aligned process implementation",
        "Automated workflow deployment",
        "KPI tracking and optimization",
        "Service desk enhancement",
        "Continuous improvement programs"
      ]
    },
    {
      icon: Cloud,
      title: "Technology Modernization",
      description: "Transform your infrastructure with cutting-edge solutions",
      details: [
        "Cloud migration and optimization",
        "Infrastructure modernization",
        "Digital transformation initiatives",
        "Technology stack optimization",
        "Innovation roadmap development"
      ]
    },
    {
      icon: HeartHandshake,
      title: "Strategic Partnership",
      description: "Align IT with business objectives",
      details: [
        "Regular strategy alignment sessions",
        "Technology roadmap development",
        "Budget optimization consulting",
        "Vendor relationship management",
        "Executive-level advisory services"
      ]
    },
    {
      icon: Settings,
      title: "Operational Excellence",
      description: "Enhance IT service delivery and efficiency",
      details: [
        "Service level agreement management",
        "Performance monitoring and optimization",
        "Capacity planning and management",
        "Change management support",
        "Best practices implementation"
      ]
    }
  ],
  stats: [
    {
      icon: Users,
      value: "200+",
      label: "IT Teams Empowered"
    },
    {
      icon: ChartBar,
      value: "99.9%",
      label: "Client Satisfaction"
    },
    {
      icon: Clock,
      value: "15min",
      label: "Response Time"
    },
    {
      icon: Brain,
      value: "15+",
      label: "Years Experience"
    }
  ],
  process: [
    {
      number: 1,
      title: "Assessment",
      description: "Comprehensive evaluation of your current IT capabilities, gaps, and needs"
    },
    {
      number: 2,
      title: "Strategy",
      description: "Development of a tailored co-managed IT partnership plan"
    },
    {
      number: 3,
      title: "Integration",
      description: "Seamless onboarding and team integration process"
    },
    {
      number: 4,
      title: "Optimization",
      description: "Continuous improvement and adaptation of services"
    }
  ],
  pricing: [
    {
      title: "Essential Support",
      price: "$2,499/mo",
      description: "Perfect for small to medium IT teams",
      features: [
        "24/7 Help Desk Support",
        "Network & Infrastructure Monitoring",
        "Security Services & Monitoring",
        "Monthly Performance Reports",
        "Basic Process Automation",
        "Email & Phone Support"
      ]
    },
    {
      title: "Professional",
      price: "$4,999/mo",
      description: "Ideal for growing organizations",
      features: [
        "Everything in Essential Support",
        "Dedicated Technical Account Manager",
        "Advanced Security Services",
        "Project Management Support",
        "Quarterly Business Reviews",
        "Priority Response SLA",
        "Training & Documentation",
        "Cloud Services Management"
      ],
      recommended: true
    },
    {
      title: "Enterprise",
      price: "Custom",
      description: "Full-scale partnership for large organizations",
      features: [
        "Everything in Professional",
        "Dedicated Support Team",
        "Custom Integration Solutions",
        "Executive Advisory Services",
        "24/7 Priority Support",
        "Compliance Management",
        "Innovation Planning",
        "Strategic IT Consulting"
      ]
    }
  ],
  testimonials: [
    {
      quote: "UCX has transformed our IT operations. Their co-managed services have given us the flexibility to scale our capabilities while maintaining control of our infrastructure. The expertise they bring to the table is invaluable.",
      author: "Jennifer Lee",
      role: "IT Director",
      company: "Enterprise Solutions Inc."
    },
    {
      quote: "The level of support and expertise we receive from UCX is exceptional. They've become an integral part of our IT strategy, helping us navigate complex technology challenges while upskilling our internal team.",
      author: "Robert Wilson",
      role: "CIO",
      company: "Dynamic Systems Corporation"
    },
    {
      quote: "Working with UCX has been a game-changer for our IT department. Their co-managed approach gives us the perfect balance of support and autonomy, allowing us to focus on strategic initiatives while knowing our day-to-day operations are in good hands.",
      author: "Mark Thompson",
      role: "Technical Director",
      company: "Innovation Tech Solutions"
    }
  ]
};

export default function CoManagedIT() {
  return <ServicePage {...coManagedITData} />;
}